// Pages/CreateOrganization.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chrome from "@uiw/react-color-chrome";
import { GithubPlacement } from "@uiw/react-color-github";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../helper/firebaseClient";
import { doc, setDoc } from "firebase/firestore";
import CircularProgress from "@mui/material/CircularProgress";
import toast, { Toaster } from "react-hot-toast";
import Papa from "papaparse";
import logoName from "../assets/logo.png";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { DEPARTMENT_COLORS } from "../styles/departmentColors";
import { DEFAULT_DEPARTMENTS } from "../constants/defaultDepartments";

const PRICE_MAP = {
  starter: {
    monthly: 399,
    yearly: 4788,
  },
  basic: {
    monthly: 699,
    yearly: 8388,
  },
  pro: {
    monthly: 1499,
    yearly: 17988,
  },
};

const steps = [
  { id: 1, name: "Organization Details", icon: "📋" },
  { id: 2, name: "Invite Team", icon: "👥" },
  { id: 3, name: "Complete", icon: "✅" },
];

const CreateOrganization = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planCode, isYearly } = location.state || {};
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orgId, setOrgId] = useState(null);

  useEffect(() => {
    if (!planCode) {
      toast.error("Please select a plan first");
      navigate("/#pricing");
    }
  }, [planCode]);

  // Organization Details
  const [formData, setFormData] = useState({
    organizationName: "",
    organizationLogo: null,

    firstName: "",
    lastName: "",
    username: "",
    profilePicture: null,

    availableSeats: "",
    description: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const billingCycle = isYearly ? "yearly" : "monthly";
  const pricePerSeat = PRICE_MAP[planCode]?.[billingCycle] || 0;
  const totalPrice = Number(formData.availableSeats || 0) * pricePerSeat;
  const totalSeats = Number(formData.availableSeats || 0);
  const inviteSeats = Math.max(totalSeats - 1, 0); //

  // Team Invites
  const [teamEmails, setTeamEmails] = useState([""]);
  const [teamRoles, setTeamRoles] = useState([""]); // Store roles for each email
  const [csvFile, setCsvFile] = useState(null);
  const [inviteMethod, setInviteMethod] = useState("manual"); // "manual" or "csv"
  const [roles, setRoles] = useState([]);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [newDepartmentColor, setNewDepartmentColor] = useState("#8B5CF6");
  const [departments, setDepartments] = useState(
    DEFAULT_DEPARTMENTS.map((d) => ({ ...d, isDefault: true }))
  );
  const keptDefaultDepartments = departments
    .filter((d) => d.isDefault) // only defaults
    .map((d) => d.name); // send only names
  const [pendingDepartments, setPendingDepartments] = useState([]);
  const [teamDepartments, setTeamDepartments] = useState([[]]);
  const [openDeptDropdown, setOpenDeptDropdown] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // If password is deleted, also clear confirmPassword
      if (field === "password" && !value) {
        updated.confirmPassword = "";
      }

      return updated;
    });
    setError(null);
  };

  const handleConfirmPasswordFocus = () => {
    if (!formData.password) {
      setError("Please fill password first");
    } else {
      setError(null);
    }
  };

  const handleFileChange = (field, file) => {
    setFormData({ ...formData, [field]: file });
  };

  const handleDepartmentChange = (index, deptId) => {
    if (teamRoles[index] === "super_admin") return;

    setTeamDepartments((prev) => {
      const updated = [...prev];
      const current = updated[index] || [];

      updated[index] = current.includes(deptId)
        ? current.filter((id) => id !== deptId)
        : [...current, deptId];

      return updated;
    });
  };

  const handleAddEmail = () => {
    const validEmails = teamEmails.filter(
      (email) => email.trim() && email.includes("@")
    );

    if (validEmails.length >= inviteSeats) {
      setError(`You can only invite up to ${inviteSeats} team member(s).`);
      toast.error(
        `Maximum ${inviteSeats} invite(s) allowed based on available seats.`
      );
      return;
    }

    setTeamEmails([...teamEmails, ""]);
    setTeamRoles([...teamRoles, ""]);
    setTeamDepartments([...teamDepartments, []]);
    setError(null);
  };

  const handleRemoveEmail = (index) => {
    setTeamEmails(teamEmails.filter((_, i) => i !== index));
    setTeamRoles(teamRoles.filter((_, i) => i !== index));
    setTeamDepartments(teamDepartments.filter((_, i) => i !== index));
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...teamEmails];
    newEmails[index] = value;
    setTeamEmails(newEmails);
  };

  const handleRoleChange = (index, value) => {
    const previousRole = teamRoles[index]; // capture old role

    // update roles
    setTeamRoles((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });

    // update departments
    setTeamDepartments((prev) => {
      const updated = [...prev];

      if (value === "super_admin") {
        // assign all departments
        updated[index] = departments.map((d) => d.id);
      } else if (previousRole === "super_admin") {
        // ONLY clear if coming FROM super_admin
        updated[index] = [];
      }
      // else → keep existing selection (DO NOTHING)

      return updated;
    });
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const emails = [];
          const roles = [];

          results.data.forEach((row) => {
            const email = row.email || row.Email || row.EMAIL;
            const role = row.role || row.Role || row.ROLE || "";

            if (email && email.includes("@")) {
              emails.push(email);
              roles.push(role);
            }
          });

          if (emails.length > 0) {
            if (emails.length > inviteSeats) {
              toast.error(
                `CSV contains ${emails.length} emails, but only ${inviteSeats} seat(s) available.`
              );
              setTeamEmails(emails.slice(0, inviteSeats));
              setTeamRoles(roles.slice(0, inviteSeats));
            } else {
              setTeamEmails(emails);
              setTeamRoles(roles);
              toast.success(`Loaded ${emails.length} emails from CSV`);
            }
          } else {
            toast.error("No valid emails found in CSV");
          }
        },
        error: (error) => {
          toast.error("Error parsing CSV: " + error.message);
        },
      });
    }
  };

  const validateStep1 = () => {
    if (!formData.organizationName.trim()) {
      setError("Organization name is required");
      return false;
    }
    if (!formData.profilePicture) {
      setError("Profile Picture is required");
      return false;
    }
    if (!formData.organizationLogo) {
      setError("Organization logo is required");
      return false;
    }
    if (!formData.firstName || !formData.lastName) {
      setError("First and Last name are required");
      return false;
    }
    if (!formData.username.trim()) {
      setError("Username is required");
      return false;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Invalid email format");
      return false;
    }
    if (!formData.password) {
      setError("Password is required");
      return false;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const validIndexes = teamEmails
      .map((email, i) => ({ email, i }))
      .filter(({ email }) => email.trim() && email.includes("@"));

    if (validIndexes.length > inviteSeats) {
      setError(`You can only invite up to ${inviteSeats} team member(s).`);
      return false;
    }

    for (const { i } of validIndexes) {
      const role = teamRoles[i];
      const depts = teamDepartments[i] || [];

      if (!role) {
        setError("Please select a role for all invited members");
        return false;
      }

      if (role !== "super_admin" && depts.length === 0) {
        setError(
          "Please select at least one department for non-super_admin roles"
        );
        return false;
      }
    }

    return true;
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!validateStep1()) {
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (!validateStep2()) {
        return;
      }
      await handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const uploadImage = async (file, path) => {
    const storage = getStorage();
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleCreateDepartment = () => {
    if (!newDepartmentName.trim()) {
      toast.error("Department name is required");
      return;
    }

    const tempDept = {
      id: `temp-${Date.now()}`,
      name: newDepartmentName,
      metadata: {
        bg: newDepartmentColor,
        border: newDepartmentColor,
      },
      isDefault: false,
    };

    setDepartments((prev) => [...prev, tempDept]);
    setPendingDepartments((prev) => [...prev, tempDept]);

    setNewDepartmentName("");
    setNewDepartmentColor("#8B5CF6");
    setShowAddDepartmentModal(false);

    toast.success("Department will be created after payment");
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      const token = await userCredential.user.getIdToken();

      let orgLogoUrl = "";
      let profilePicUrl = "";

      if (formData.organizationLogo) {
        orgLogoUrl = await uploadImage(
          formData.organizationLogo,
          `org-logos/${user.uid}`
        );
      }

      if (formData.profilePicture) {
        profilePicUrl = await uploadImage(
          formData.profilePicture,
          `profile-pictures/${user.uid}`
        );
      }

      // Create organization using the new endpoint that accepts ID token
      const orgData = new FormData();
      orgData.append("idToken", token);
      orgData.append("name", formData.organizationName);
      orgData.append("availableSeats", Number(formData.availableSeats));
      orgData.append("description", formData.description || "");
      orgData.append("planCode", planCode);
      orgData.append("billingCycle", isYearly ? "yearly" : "monthly");
      orgData.append("status", "pending_payment");
      orgData.append("logoUrl", orgLogoUrl);
      orgData.append("profileUrl", profilePicUrl);
      orgData.append(
        "keptDefaultDepartments",
        JSON.stringify(keptDefaultDepartments)
      );

      const orgResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/org/createOrgWithToken`,
        orgData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const createdOrgId = orgResponse.data.id;
      setOrgId(createdOrgId);

      //  CREATE USER-ADDED DEPARTMENTS AFTER ORG CREATION
      for (const dept of pendingDepartments) {
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/dept/createDepts`,
            {
              idToken: token,
              name: dept.name,
              metadata: dept.metadata,
              org_id: createdOrgId,
            }
          );

          // Optional reconciliation if you later need real dept IDs.
          // Invites use department name, so this is not required for invites.
          const saved = res?.data;
          if (saved?.id) {
            setDepartments((prev) =>
              prev.map((d) => (d.id === dept.id ? { ...d, id: saved.id } : d))
            );
          }
        } catch (err) {
          console.error("Failed to create department:", dept.name, err);
        }
      }

      // Create user profile with organization
      await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/auth/sign-up/`,
        {
          idToken: token,
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          email: formData.email,
          password: formData.password,
          org_id: createdOrgId,
          org_name: formData.organizationName,
          uid: userCredential.user.uid,
          department: "All",
          profilePictureUrl: profilePicUrl,
          organizationLogo: orgLogoUrl,
        }
      );

      // Create userChats document
      try {
        await setDoc(
          doc(
            db,
            "organizations",
            createdOrgId,
            "userChats",
            userCredential.user.uid
          ),
          {}
        );
      } catch (error) {
        console.error("Error writing document: ", error);
      }

      // Store team invites for later processing after user is authenticated
      const validInvites = [];

      const enteredEmails = teamEmails.filter(
        (email) => email.trim() && email.includes("@")
      );

      enteredEmails.forEach((email, index) => {
        if (!teamDepartments[index] || !teamRoles[index]) {
          toast.error(
            "Please assign both department and role for all invited members"
          );
          throw new Error("Invalid invite data");
        }

        if (teamRoles[index] === "org_admin") {
          toast.error("You cannot assign org_admin role");
          throw new Error("Invalid role assignment");
        }

        let deptNames; // array of department NAME strings
        let deptLegacy; // legacy single string kept for backward compat

        if (teamRoles[index] === "super_admin") {
          deptNames = departments.map((d) => d.name);
          deptLegacy = "All";
        } else {
          // employee → map selected temp-IDs / real IDs to department names
          deptNames = (teamDepartments[index] || [])
            .map((id) => {
              const match = departments.find((d) => d.id === id);
              return match?.name || id;
            })
            .filter(Boolean);

          // Legacy fallback string (first name only) — kept so old backend
          // code that reads invite.dept still gets something meaningful.
          deptLegacy = deptNames[0] || "";
        }

        validInvites.push({
          email: email.trim(),
          role: teamRoles[index], // role name like "employee"
          // FIX: send ARRAY of names, not a comma-joined single string
          dept_names: deptNames,
          // legacy single-value field — still sent so _resolve_dept fallback works
          dept: deptLegacy,
        });
      });

      if (validInvites.length > 0) {
        try {
          await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/invite`, {
            orgId: createdOrgId,
            invites: validInvites,
            batchId: `org-${createdOrgId}-invites`,
            idToken: token,
          });

          toast.success(
            `${validInvites.length} team member(s) queued for invitation`
          );
        } catch (err) {
          console.error("Failed to store invites:", err);
          toast.error("Failed to queue team invites");
        }
      }

      setCurrentStep(3);
      toast.success("Organization created successfully!");
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "An error occurred. Please try again."
      );
      toast.error("Failed to create organization");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate("/checkout", {
      state: {
        orgId,
        planCode,
        billingCycle: isYearly ? "yearly" : "monthly",
        seats: Number(formData.availableSeats),
        isRenewal: false,
      },
    });
  };

  // Close dept dropdown on outside click
  useEffect(() => {
    if (openDeptDropdown === null) return;
    const handler = (e) => {
      if (!e.target.closest(".dept-dropdown-root")) {
        setOpenDeptDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openDeptDropdown]);

  // Fetch departments and roles on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch roles (public endpoint)
        const rolesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/role/listRoles`
        );
        setRoles(rolesResponse.data || []);
        setDepartments(
          DEFAULT_DEPARTMENTS.map((d) => ({
            ...d,
            isDefault: true,
          }))
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load roles");
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F7]">
      <Toaster position="top-right" />

      {/* Header with Logo */}
      <nav className="bg-[#FAF8F7] h-[72px] flex items-center px-4 md:px-12 font-tahoma">
        <NavLink
          to={"/"}
          alt="Twokey"
          className="flex items-center gap-2 transition-transform duration-200 hover:scale-105"
        >
          <img
            src={logoName}
            alt="Twokey"
            className="h-8 md:h-10 object-contain"
          />
          <span
            className="text-lg font-[400] tracking-wide"
            style={{ fontFamily: "Times New Roman, serif" }}
          >
            TwoKey
          </span>
        </NavLink>
      </nav>

      {/* Main Content */}
      <div className="flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-8 md:p-12">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-semibold transition-all duration-300 ${
                        currentStep >= step.id
                          ? "bg-[#5E5ADB] text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {currentStep > step.id ? "✓" : step.icon}
                    </div>
                    <p
                      className={`mt-2 text-sm font-medium ${
                        currentStep >= step.id
                          ? "text-[#5E5ADB]"
                          : "text-gray-500"
                      }`}
                    >
                      {step.name}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                        currentStep > step.id ? "bg-[#5E5ADB]" : "bg-gray-200"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">
              Create Your Organization
            </h1>
            <p className="text-gray-600 mb-8">
              {currentStep === 1 && "Let's get your workspace set up"}
              {currentStep === 2 && "Invite your team members"}
              {currentStep === 3 && "Your organization is ready!"}
            </p>

            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                    placeholder="Acme Corporation"
                    value={formData.organizationName}
                    onChange={(e) =>
                      handleInputChange("organizationName", e.target.value)
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Organization Logo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleFileChange("organizationLogo", e.target.files[0])
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Profile Picture <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleFileChange("profilePicture", e.target.files[0])
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                    placeholder="A few words about your Organization..."
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) =>
                        handleInputChange("firstName", e.target.value)
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      readOnly={!formData.password}
                      onFocus={handleConfirmPasswordFocus}
                      onChange={(e) =>
                        handleInputChange("confirmPassword", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total quantity of seats required
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="w-full px-4 py-2 border rounded-lg"
                    value={formData.availableSeats}
                    onChange={(e) =>
                      handleInputChange("availableSeats", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()} // scroll disable
                  />

                  {/* </div> */}
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500 mt-1">
                      Set how many team members you want to invite
                    </p>

                    {formData.availableSeats > 0 && pricePerSeat > 0 && (
                      <p className="text-xs text-red-600 font-medium">
                        {formData.availableSeats} seat(s) × ₹{pricePerSeat} /
                        user
                        <span className=" text-xs font-bold text-red-600">
                          {" "}
                          = ₹{totalPrice}
                        </span>{" "}
                        <span className="text-gray-500">
                          (to be paid at checkout)
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowAddDepartmentModal(true)}
                      className="px-4 py-2 bg-[#5E5ADB] text-white text-sm rounded-lg hover:bg-[#4C47C7] transition-all font-medium"
                    >
                      Add New Department
                    </button>
                  </div>

                  {departments.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                      <p className="text-gray-500 mb-3 text-sm">
                        No departments yet
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowAddDepartmentModal(true)}
                        className="text-[#5E5ADB] hover:underline text-sm font-medium"
                      >
                        Create your first department
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto p-1">
                      {departments.map((dept) => {
                        const dotColor = dept.metadata?.bg || "#6366F1";

                        return (
                          <div
                            key={dept.id}
                            className=" group p-4 rounded-2xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all duration-200"
                            // style={{
                            //   backgroundColor:
                            //     dept.color || dept.metadata?.bg || "#F5F5F5",
                            // }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span
                                  // className="w-4 h-4 rounded-full flex-shrink-0"
                                  className="w-4 h-4 rounded-full ring-2 ring-white shadow-sm"
                                  style={{ backgroundColor: dotColor }}
                                />
                                <span className="font-semibold text-gray-800 text-sm">
                                  {dept.name}
                                </span>
                              </div>

                              <button
                                onClick={() =>
                                  setDepartments(
                                    departments.filter((d) => d.id !== dept.id)
                                  )
                                }
                                className="text-red-500 text-sm hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex gap-4 mb-6">
                  <button
                    onClick={() => setInviteMethod("manual")}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                      inviteMethod === "manual"
                        ? "bg-[#5E5ADB] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Add Emails Manually
                  </button>
                  <button
                    onClick={() => setInviteMethod("csv")}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                      inviteMethod === "csv"
                        ? "bg-[#5E5ADB] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Upload CSV
                  </button>
                </div>

                {inviteMethod === "manual" && (
                  <div className="space-y-4">
                    {teamEmails.map((email, index) => {
                      return (
                        <div key={index} className="flex gap-2">
                          <input
                            type="email"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                            placeholder="team@example.com"
                            value={email}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              const otherValidEmails = teamEmails.filter(
                                (e, i) =>
                                  i !== index && e.trim() && e.includes("@")
                              );
                              // const availableSeats = Number(
                              //   formData.availableSeats || 0
                              // );

                              // Check if adding this email would exceed seat limit
                              if (
                                newValue.trim() &&
                                newValue.includes("@") &&
                                !email.includes("@")
                              ) {
                                if (otherValidEmails.length >= inviteSeats) {
                                  toast.error(
                                    `Maximum ${inviteSeats} invite(s) allowed based on available seats.`
                                  );
                                  return;
                                }
                              }
                              handleEmailChange(index, newValue);
                            }}
                          />

                          {/* Department multi-select dropdown */}
                          {(() => {
                            const isSuperAdmin =
                              teamRoles[index] === "super_admin";
                            const selectedIds = isSuperAdmin
                              ? departments.map((d) => d.id)
                              : teamDepartments[index] ?? [];
                            const label = isSuperAdmin
                              ? "All Departments ✓"
                              : selectedIds.length === 0
                                ? "Select Department(s)"
                                : selectedIds.length === departments.length
                                  ? "All Departments"
                                  : selectedIds
                                      .map(
                                        (id) =>
                                          departments.find((d) => d.id === id)
                                            ?.name || id
                                      )
                                      .join(", ");

                            return (
                              <div className="relative dept-dropdown-root">
                                <button
                                  type="button"
                                  onClick={() =>
                                    isSuperAdmin
                                      ? null
                                      : setOpenDeptDropdown(
                                          openDeptDropdown === index
                                            ? null
                                            : index
                                        )
                                  }
                                  className={`px-3 py-2 border rounded-lg text-left text-sm min-w-[160px] max-w-[200px] truncate ${
                                    isSuperAdmin
                                      ? "bg-indigo-50 border-indigo-300 text-indigo-700 cursor-default"
                                      : "border-gray-300 bg-white hover:border-[#5E5ADB] cursor-pointer"
                                  }`}
                                  title={label}
                                >
                                  {label}
                                  {!isSuperAdmin && (
                                    <span className="ml-1 text-gray-400">
                                      ▾
                                    </span>
                                  )}
                                </button>

                                {openDeptDropdown === index &&
                                  !isSuperAdmin && (
                                    <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] max-h-52 overflow-y-auto">
                                      {departments.map((dept) => {
                                        const checked = selectedIds.includes(
                                          dept.id
                                        );
                                        return (
                                          <label
                                            key={dept.id}
                                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() =>
                                                handleDepartmentChange(
                                                  index,
                                                  dept.id
                                                )
                                              }
                                              className="accent-[#5E5ADB]"
                                            />
                                            <span
                                              className="w-3 h-3 rounded-full flex-shrink-0"
                                              style={{
                                                backgroundColor:
                                                  dept.metadata?.bg ||
                                                  "#6366F1",
                                              }}
                                            />
                                            <span>{dept.name}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                              </div>
                            );
                          })()}

                          {/* <select
                            value={teamDepartments[index] || ""}
                            onChange={(e) =>
                              handleDepartmentChange(index, e.target.value)
                            }
                            className="px-3 py-2 border rounded-lg"
                            required
                          >
                            <option value="" disabled hidden>
                              Select Department
                            </option>

                            {departments.map((dept) => (
                              <option key={dept.id} value={dept.id}>
                                {dept.name} {dept.emoji}
                              </option>
                            ))}
                          </select> */}

                          <select
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                            value={teamRoles[index] || ""}
                            onChange={(e) =>
                              handleRoleChange(index, e.target.value)
                            }
                            required
                          >
                            <option value="" disabled hidden>
                              Select Role
                            </option>

                            {roles
                              .filter((role) => role.role !== "org_admin") // hide org_admin
                              .map((role) => (
                                <option key={role.id} value={role.role}>
                                  {role.role}
                                </option>
                              ))}
                          </select>
                          {teamEmails.length > 1 && (
                            <button
                              onClick={() => handleRemoveEmail(index)}
                              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <button
                      onClick={handleAddEmail}
                      className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#5E5ADB] hover:text-[#5E5ADB] transition-all"
                    >
                      + Add Another Email
                    </button>
                    {(() => {
                      const availableSeats = Number(
                        formData.availableSeats || 0
                      );
                      const validEmails = teamEmails.filter(
                        (email) => email.trim() && email.includes("@")
                      );
                      const remaining = inviteSeats - validEmails.length;
                      return (
                        <p className="text-sm text-gray-600 mt-2">
                          {remaining > 0
                            ? `${remaining} seat(s) remaining out of ${availableSeats}. (𝟏 seat is reserved for you)`
                            : `All ${availableSeats} seat(s) have been used.  (𝟏 seat is reserved for you)`}
                        </p>
                      );
                    })()}
                  </div>
                )}

                {inviteMethod === "csv" && (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleCsvUpload}
                        className="hidden"
                        id="csv-upload"
                      />
                      <label
                        htmlFor="csv-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <svg
                          className="w-12 h-12 text-gray-400 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p className="text-gray-600 mb-2">
                          Click to upload CSV file
                        </p>
                        <p className="text-sm text-gray-400">
                          CSV should contain "email" and optionally "role"
                          columns
                        </p>
                      </label>
                    </div>
                    {csvFile && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-800">
                          ✓ {csvFile.name} uploaded successfully
                        </p>
                        <p className="text-sm text-green-600 mt-1">
                          {teamEmails.length} email(s) found
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    💡 You can skip this step and invite team members later from
                    your dashboard.
                  </p>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-12 h-12 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-4">
                  Organization Created Successfully!
                </h2>
                <p className="text-gray-600 mb-8">
                  Your organization has been set up. Please proceed to checkout
                  to activate your plan.
                </p>
              </div>
            )}

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-6 border-t">
            <button
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                currentStep === 1 || loading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Back
            </button>

            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                disabled={loading}
                className="px-6 py-2 bg-[#5E5ADB] text-white rounded-lg font-medium hover:bg-[#4C47C7] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} style={{ color: "white" }} />
                    <span>Processing...</span>
                  </>
                ) : currentStep === 2 ? (
                  "Create Organization"
                ) : (
                  "Next"
                )}
              </button>
            ) : (
              <button
                onClick={handleComplete}
                className="px-6 py-2 bg-[#5E5ADB] text-white rounded-lg font-medium hover:bg-[#4C47C7] transition-all"
              >
                Go to Checkout
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Department Creation Modal */}
      <Dialog
        open={showAddDepartmentModal}
        onClose={() => setShowAddDepartmentModal(false)}
        PaperProps={{
          style: {
            borderRadius: "12px",
            minWidth: "420px",
            maxWidth: "480px",
          },
        }}
      >
        <DialogTitle className="text-xl font-semibold">
          Create New Department
        </DialogTitle>
        <DialogContent style={{ backgroundColor: "#F7F8FA" }}>
          <div className="my-2 w-full py-2 space-y-4">
            <span className="w-full block">
              <p className="text-gray-700 mb-2">
                Department Name <span className="text-red-500">*</span>
              </p>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                type="text"
                placeholder="e.g. Engineering"
                value={newDepartmentName}
                onChange={(e) => setNewDepartmentName(e.target.value)}
              />
            </span>

            <div>
              <p className="text-gray-700 mb-3">Color</p>
              {/* <div className="grid grid-cols-6 gap-2"> */}
              <div className="grid grid-cols-6 sm:grid-cols-7 gap-3">
                {DEPARTMENT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewDepartmentColor(color)}
                    className={`relative w-11 h-11 rounded-xl transition-all duration-200 hover:scale-110 hover:shadow-lg active:scale-95`}
                    style={{
                      backgroundColor: color,
                      border:
                        newDepartmentColor === color
                          ? "3px solid #111827"
                          : "2px solid rgba(255,255,255,0.9)",
                      boxShadow:
                        newDepartmentColor === color
                          ? "0 0 0 3px rgba(99,102,241,0.35)"
                          : "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    {newDepartmentColor === color && (
                      <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-3">
                <span
                  className="w-8 h-8 rounded-full border border-gray-200 flex-shrink-0"
                  style={{ backgroundColor: newDepartmentColor }}
                />
                <p className="text-sm text-gray-600">
                  Selected: <span className="font-mono font-medium">{newDepartmentColor}</span>
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <button
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            onClick={() => {
              setShowAddDepartmentModal(false);
              setNewDepartmentName("");
              setNewDepartmentColor("#8B5CF6");
            }}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-[#5E5ADB] text-white hover:bg-[#4C47C7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCreateDepartment}
            disabled={!newDepartmentName.trim()}
          >
            Add Department
          </button>
        </DialogActions>
      </Dialog>

      {/* <Dialog
        open={showAddDepartmentModal}
        onClose={() => setShowAddDepartmentModal(false)}
        PaperProps={{
          style: {
            borderRadius: "12px",
            minWidth: "600px",
          },
        }}
      >
        <DialogTitle className="text-xl font-semibold">
          New Department
        </DialogTitle>
        <DialogContent
          style={{
            backgroundColor: "#F7F8FA",
          }}
        >
          <div className="my-2 w-full py-2">
            <span className="w-full">
              <p className="text-gray-700 mb-2">Department Name</p>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#5E5ADB] focus:border-transparent"
                type="text"
                placeholder="Enter department name"
                value={newDepartmentName}
                onChange={(e) => setNewDepartmentName(e.target.value)}
              />
            </span>

            <div className="w-full flex gap-6 mt-4">
              <span className="flex-1">
                <p className="text-gray-700 mb-2">
                  Department Icon{" "}
                  {newDepartmentEmoji && `: ${newDepartmentEmoji}`}
                </p>
                <Picker
                  data={data}
                  set="native"
                  onEmojiSelect={(emoji) => setNewDepartmentEmoji(emoji.native)}
                  theme="light"
                  perLine={7}
                />
              </span>

              <span className="flex-1">
                <p className="text-gray-700 mb-2">Department Color</p>
                <Chrome
                  color={newDepartmentColor}
                  style={{ width: "100%", margin: "auto" }}
                  placement={GithubPlacement.Right}
                  onChange={(color) => {
                    setNewDepartmentColor(color.hexa);
                  }}
                />
              </span>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <button
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            onClick={() => {
              setShowAddDepartmentModal(false);
              setNewDepartmentName("");
              setNewDepartmentEmoji("📁");
              setNewDepartmentColor("#4F46E5");
            }}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-[#5E5ADB] text-white hover:bg-[#4C47C7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            //
            onClick={handleCreateDepartment}
            disabled={!newDepartmentName.trim()}
          >
            Add Department
          </button>
        </DialogActions>
      </Dialog> */}
    </div>
  );
};

export default CreateOrganization;
